import React, { useEffect, useState } from "react";
import axios from "axios";
import { differenceWith, isEmpty, xor } from "lodash";
import {
  SdfAlert,
  SdfButton,
  SdfExpandableBox,
  SdfFocusPane,
  SdfLink,
  SdfSpotIllustration,
  SdfSwitch,
  SdfTextarea,
} from '@synerg/react-components'

export const ReviewAuthTextStep = (props = {}) => {
  const {
    isB2bScopes,
    data,
    setData,
    setConsentsUpdated,
    preSelectedAuthTexts = [],
  } = props;

  const [authText, setAuthText] = useState([]);
  const [proposedAuthText, setProposedAuthText] = useState([]);

  const [customAuthTextModal, setCustomAuthTextModal] = useState(false); // modal show hide flag
  const [customSuggestedText, setCustomSuggestedText] = useState(""); // value holder
  const [sameAuthExistsError, setSameAuthExistsError] = useState(false); // text area error

  useEffect(() => {
    getProposedAuthText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAuthText(
      preSelectedAuthTexts.map((item) => ({
        expanded: false,
        applicable: true,
        value: item,
        header: item,
      })) || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedAuthTexts]);

  useEffect(() => {
    setConsentsUpdated(
      !isEmpty(
        xor(
          [
            ...proposedAuthText
              .filter((item) => item.applicable)
              .map((itm) => itm.value),
            ...authText
              .filter((item) => item.applicable)
              .map((itm) => itm.value),
          ],
          preSelectedAuthTexts
        )
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedAuthText, authText]);

  useEffect(() => {
    // if use checked the switch than inculde to list
    const authTextApplicable = authText
      .filter((item) => item.applicable)
      .map((itm) => itm.value);
    const proposedTextApplicable = proposedAuthText
      .filter((item) => item.applicable)
      .map((itm) => itm.value);

    setData((prevState) => ({
      ...prevState,
      proposedConsentTexts: [...authTextApplicable, ...proposedTextApplicable],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedAuthText, authText]);

  const getProposedAuthText = async () => {
    const {
      data: { canonicalConsentTexts = [] },
    } = await axios.post(
      `${import.meta.env.VITE_ROOT_PATH}/api/canonical-consent-texts`,
      {
        canonicalsToSearch: isB2bScopes
          ? data.b2bApplicationScopes
          : data.b2cApplicationScopes,
      }
    );
    if (canonicalConsentTexts.length > 0) {
      const filteredSuggestedConsents = differenceWith(
        canonicalConsentTexts,
        preSelectedAuthTexts,
        (x, y) => x.toLowerCase() === y.toLowerCase()
      );
      setProposedAuthText(
        (filteredSuggestedConsents || []).map((item) => ({
          expanded: false,
          applicable: false,
          value: item,
          header: item,
        }))
      );
    }
  };

  const switchChangeHandler = (event, index, type) => {
    if (type === "today") {
      const tmpAuthText = [...authText];
      tmpAuthText[index]["applicable"] = event.detail;
      setAuthText(tmpAuthText);
    } else if (type === "proposed") {
      const tmpProposedAuthText = [...proposedAuthText];
      tmpProposedAuthText[index]["applicable"] = event.detail;
      setProposedAuthText(tmpProposedAuthText);
    }
  };

  const collapseExpandableBox = (index, type) => {
    if (type === "today") {
      const tmpAuthText = [...authText];
      tmpAuthText[index]["expanded"] = false;
      setAuthText(tmpAuthText);
    } else if (type === "proposed") {
      const tmpProposedAuthText = [...proposedAuthText];
      tmpProposedAuthText[index]["expanded"] = false;
      setProposedAuthText(tmpProposedAuthText);
    }
  };

  const openExpandableBox = (index, type) => {
    if (type === "today") {
      const tmpAuthText = [...authText];
      tmpAuthText[index]["expanded"] = true;
      setAuthText(tmpAuthText);
    } else if (type === "proposed") {
      const tmpProposedAuthText = [...proposedAuthText];
      tmpProposedAuthText[index]["expanded"] = true;
      setProposedAuthText(tmpProposedAuthText);
    }
  };

  const expandTodayAuthBox = () => {
    setAuthText(authText.map((item) => ({ ...item, expanded: true })));
  };

  const expandProposedAuthBox = () => {
    setProposedAuthText(
      proposedAuthText.map((item) => ({ ...item, expanded: true }))
    );
  };

  const addCustomAuthText = () => {
    if (customSuggestedText) {
      const proposedTexts = proposedAuthText.map((item) => item.value);
      if (!proposedTexts.includes(customSuggestedText)) {
        const authData = [
          ...proposedAuthText,
          {
            expanded: false,
            applicable: false,
            value: customSuggestedText,
            header: customSuggestedText,
          },
        ];
        setProposedAuthText(authData);
        setCustomAuthTextModal(false);
        setCustomSuggestedText("");
      } else {
        setSameAuthExistsError(true);
      }
    }
  };

  return (
    <div className="pr-10">
      <div className="text-heading-05 font-bold">
        Review suggested {isB2bScopes ? "B2B" : "B2C"} authorization text
      </div>
      <div className="text-body-02">
        Select to add suggested text to project, deselect to remove and/or write
        your own.
      </div>

      <div className="flex w-full mt-6 h-lg">
        <div className="w-2/4 pr-4">
          <div className="flex justify-between items-center">
            <div className="text-heading-05 font-bold">Auth text today</div>
            <div className="">
              <SdfButton
                variant={"text"}
                onClick={expandTodayAuthBox}
                disabled={authText.length > 0 ? undefined : true}
              >
                Expand all
              </SdfButton>
            </div>
          </div>
          {authText.length > 0 ? (
            <div className="">
              {authText.map((item, index) => (
                <SdfExpandableBox
                  key={"authTextsdfExpandableBox_" + index}
                  triggerPlacement="after"
                  variant="bordered"
                  className="mt-5"
                  opened={item.expanded}
                  onSdfClose={() => collapseExpandableBox(index, "today")}
                  onSdfOpen={() => openExpandableBox(index, "today")}
                >
                  <div slot="header" className="flex">
                    <div>
                      <SdfSwitch
                        checked={item.applicable}
                        data-sdf-squash=""
                        onSdfChange={(event) =>
                          switchChangeHandler(event, index, "today")
                        }
                      />
                    </div>
                    <div className="text-heading-03 font-bold flex-1 truncate overflow-ellipsis">
                      {item.header}
                    </div>
                  </div>

                  <div className="">{item.value}</div>
                </SdfExpandableBox>
              ))}
            </div>
          ) : (
            <div className="mt-6 text-center">
              <SdfSpotIllustration illustrationName="empty-checkmark" />
              <div className="color-grey mt-8">
                No authorization text present.
              </div>
            </div>
          )}
        </div>
        <div className="w-2/4 pl-4">
          <div className="flex justify-between items-center">
            <div className="text-heading-05 font-bold">
              Suggested auth text to be added
            </div>
            <div className="">
              <SdfButton
                variant={"text"}
                onClick={expandProposedAuthBox}
                disabled={proposedAuthText.length > 0 ? undefined : true}
              >
                Expand all
              </SdfButton>
            </div>
          </div>
          {proposedAuthText.length > 0 ? (
            <div className="">
              {proposedAuthText.map((item, index) => (
                <SdfExpandableBox
                  key={"sdfExpandableBox_" + index}
                  triggerPlacement="after"
                  variant="bordered"
                  className="mt-5"
                  opened={item.expanded}
                  onSdfClose={() => collapseExpandableBox(index, "proposed")}
                  onSdfOpen={() => openExpandableBox(index, "proposed")}
                >
                  <div slot="header" className="flex">
                    <div>
                      <SdfSwitch
                        checked={item.applicable}
                        data-sdf-squash=""
                        onSdfChange={(event) =>
                          switchChangeHandler(event, index, "proposed")
                        }
                      />
                    </div>
                    <div className="text-heading-03 font-bold flex-1 truncate overflow-ellipsis">
                      {item.header}
                    </div>
                  </div>

                  <div className="">{item.value}</div>
                </SdfExpandableBox>
              ))}
            </div>
          ) : (
            <div className="mt-6 text-center">
              <SdfSpotIllustration illustrationName="empty-checkmark" />
              <div className="color-grey mt-8">
                Congrats! No new authorization text is required
              </div>
            </div>
          )}
          <div className="mt-4">
            <SdfButton
              emphasis={"tertiary"}
              icon="action-add"
              onClick={() => setCustomAuthTextModal(true)}
            >
              Add custom text
            </SdfButton>
          </div>
        </div>
      </div>

      <SdfFocusPane
        visible={customAuthTextModal}
        closeable={true}
        dismissOnClickOutside={true}
        heading={"Custom authorization text"}
        hideAcceptButton={true}
        hideDismissButton={true}
        onSdfDismiss={() => setCustomAuthTextModal(false)}
        spacing={"loose"}
        size={"md"}
      >
        <div className="w-full">
          <div className="text-body-02 mt-1 mb-6">
            Copy/paste text directly from the&nbsp;
            <SdfLink
              href="https://confluence.es.ad.adp.com/pages/viewpage.action?spaceKey=aRP&title=Consent+Terms"
              target="_blank"
            >
              Confluence guide
            </SdfLink>
            &nbsp;or start writing your own custom text in the area below.
          </div>
          <SdfTextarea
            name="message"
            hideLabel={true}
            rows={6}
            onSdfInput={(event) => setCustomSuggestedText(event.detail)}
            value={customSuggestedText}
            state={sameAuthExistsError ? "error" : "normal"}
            resize="none"
          >
            {sameAuthExistsError && (
              <SdfAlert slot="alert" size="sm" status="error" type="inline">
                Same authentication text already exists.
              </SdfAlert>
            )}
          </SdfTextarea>
        </div>

        <SdfButton
          slot="custom-buttons"
          onClick={() => setCustomAuthTextModal(false)}
        >
          Cancel
        </SdfButton>
        <SdfButton
          slot="custom-buttons"
          emphasis="primary"
          onClick={() => addCustomAuthText()}
        >
          Save
        </SdfButton>
      </SdfFocusPane>
    </div>
  );
};
